import { useCallback, useMemo, useState } from "react";
import { Loader2, Mic, Radio as RadioIcon, Square } from "lucide-react";
import { useDarOrbPrefs, buzz } from "@/lib/dar-prefs";
import { cn } from "@/lib/utils";
import { DarWorkspace } from "./DarWorkspace";
import { MrVoiceWaves } from "./MrVoiceWaves";
import { useMrVoiceMicState } from "@/lib/voice-assistant/mic-state";
import { useVoiceRadioHold } from "@/hooks/useVoiceRadioHold";
import { VoiceRadioOverlay } from "./VoiceRadioOverlay";

type Phase = "idle" | "recording" | "uploading" | "review";

/**
 * Fixed centre action of the mobile bottom navigation.
 *
 * Presentation only: a short tap opens the existing DarWorkspace, which owns
 * microphone permission, recording, upload, transcription and persistence. A
 * deliberate press-and-hold engages the existing live Radio layer instead. No
 * second recording, Radio, WebRTC or Deepgram implementation exists here.
 */
export function DarNavMic() {
  const [prefs] = useDarOrbPrefs();
  const mrState = useMrVoiceMicState();
  const [open, setOpen] = useState(false);
  const [autoStart, setAutoStart] = useState(false);
  const [phase, setPhase] = useState<Phase>("idle");

  // Existing DAR tap behaviour, unchanged.
  const startDar = useCallback(() => {
    if (prefs.haptics) buzz(10);
    setAutoStart(true);
    setOpen(true);
  }, [prefs.haptics]);

  const radio = useVoiceRadioHold({ onDarTap: startDar });

  const recording = phase === "recording";
  const processing = phase === "uploading";
  const mrStandby = mrState === "standby";
  const mrLayerVisible = mrStandby || mrState === "listening" || mrState === "speaking";
  const radioOn = radio.radioEngaged;

  const label = radioOn
    ? "Radio transmitting — release to stop"
    : recording
      ? "Stop D.A.R. voice memo"
      : processing
        ? "Saving D.A.R. voice memo"
        : "Start D.A.R. voice memo. Press and hold for Radio";

  const handlePhase = useMemo(() => (p: Phase) => setPhase(p), []);

  return (
    <div className="relative flex h-14 items-end justify-center">
      <button
        type="button"
        aria-label={label}
        aria-pressed={recording || radioOn}
        {...radio.handlers}
        onClick={(e) => {
          // Keyboard/assistive activation has no pointer press: keep DAR available.
          if (e.detail !== 0) return;
          startDar();
        }}
        onContextMenu={(e) => e.preventDefault()}
        className={cn(
          "dar-nav-mic absolute -top-4 flex h-14 w-14 touch-none select-none items-center justify-center rounded-full",
          "border-2 border-[#CC0000]/75 backdrop-blur-md transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#CC0000]",
          "bg-[radial-gradient(circle_at_50%_42%,rgba(204,0,0,0.18),rgba(255,255,255,0.13)_42%,rgba(10,10,10,0.48)_100%)]",
          "shadow-[0_6px_20px_-6px_rgba(0,0,0,0.9),inset_0_1px_0_rgba(255,255,255,0.35)]",
          recording
            ? "dar-nav-mic--rec border-[#CC0000] text-white ring-2 ring-[#CC0000]/45"
            : processing
              ? "border-[#CC0000]/70 text-white/90"
              : "text-[#CC0000] ring-2 ring-[#CC0000]/45",
          // Hey MR layer — shown on the same button, never a solid fill.
          !recording && !processing && !radioOn && mrLayerVisible && "mr-mic-ring",
          !recording && !processing && !radioOn && mrState === "speaking" && "mr-mic-ring--speaking",
          radioOn && "dar-nav-mic--rec border-[#CC0000] ring-2 ring-[#CC0000]/60",
          radio.holding && !radioOn && "scale-95 ring-2 ring-[#CC0000]/70",
        )}
      >
        {!recording && !processing && !radioOn && mrLayerVisible && (
          <MrVoiceWaves speaking={mrState === "speaking"} standby={mrStandby} compact />
        )}
        {radioOn ? (
          <RadioIcon className="h-6 w-6" />
        ) : processing ? (
          <Loader2 className="h-6 w-6 animate-spin" />
        ) : recording ? (
          <Square className="h-5 w-5 fill-current" />
        ) : (
          <Mic className="h-6 w-6 drop-shadow-[0_0_8px_rgba(204,0,0,0.72)]" />
        )}
      </button>
      <span
        className={cn(
          "pointer-events-none pb-1 text-[10px] font-medium",
          recording || radioOn ? "text-[#CC0000]" : "text-[#666666]",
        )}
      >
        {radioOn ? "RADIO" : recording ? "REC" : processing ? "Saving" : "D.A.R."}
      </span>
      <VoiceRadioOverlay
        visible={radioOn}
        siteName={radio.siteName}
        phase={radio.phase}
        elapsedMs={radio.elapsedMs}
        liveTranscript={radio.liveTranscript}
        transcriptionAvailable={radio.transcriptionAvailable}
        participantCount={radio.participantCount}
        errorMessage={radio.errorMessage}
        onDismissError={radio.dismissError}
      />
      <DarWorkspace
        open={open}
        onOpenChange={setOpen}
        autoStart={autoStart}
        onPhaseChange={handlePhase}
      />
    </div>
  );
}
